import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usersApi } from '../services/api';

function strengthFromPassword(value) {
    if (!value) return { label: 'Not set', color: '#9ca3af', pct: 0 };
    let score = 0;
    if (value.length >= 8) score += 1;
    if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score += 1;
    if (/\d/.test(value)) score += 1;
    if (/[^A-Za-z0-9]/.test(value)) score += 1;
    if (value.length >= 12) score += 1;
    if (score <= 1) return { label: 'Weak', color: '#ef4444', pct: 25 };
    if (score <= 3) return { label: 'Medium', color: '#f59e0b', pct: 60 };
    if (score === 4) return { label: 'Strong', color: '#10b981', pct: 85 };
    return { label: 'Very strong', color: '#059669', pct: 100 };
}

export default function ForcePasswordChangePage() {
    const { user, updateUser, logout, isMasterDeployment } = useAuth();
    const navigate = useNavigate();

    const [form, setForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [show, setShow] = useState({ current: false, next: false, confirm: false });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const strength = strengthFromPassword(form.newPassword);

    const handleChange = (field) => (e) =>
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    const toggleShow = (field) => () =>
        setShow((prev) => ({ ...prev, [field]: !prev[field] }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!form.currentPassword || !form.newPassword || !form.confirmPassword) {
            setError('All three fields are required.');
            return;
        }
        if (form.newPassword !== form.confirmPassword) {
            setError('New password and confirmation do not match.');
            return;
        }
        if (form.newPassword.length < 6) {
            setError('New password must be at least 6 characters.');
            return;
        }
        if (form.newPassword === form.currentPassword) {
            setError('New password must be different from your current password.');
            return;
        }

        setLoading(true);
        const response = await usersApi.changePassword({
            currentPassword: form.currentPassword,
            newPassword: form.newPassword,
            confirmPassword: form.confirmPassword,
        });
        setLoading(false);

        if (!response.success) {
            setError(response.error || 'Failed to update password. Please try again.');
            return;
        }

        // Clear the forced flag in auth context so ProtectedRoute allows navigation
        if (user) {
            updateUser({ ...user, mustChangePassword: false });
        }

        // Redirect to correct home after mandatory change
        if (user?.role === 'super_admin' && isMasterDeployment) {
            navigate('/admin/master/control', { replace: true });
        } else if (['cashier', 'staff'].includes(user?.role)) {
            navigate('/admin/pos/cashier', { replace: true });
        } else {
            navigate('/admin', { replace: true });
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/admin/login', { replace: true });
    };

    const showToggleButton = (field, showKey) => (
        <button
            type="button"
            onClick={toggleShow(showKey)}
            style={{
                padding: '0 14px',
                border: '1px solid var(--line)',
                borderRadius: 8,
                background: 'var(--surface)',
                cursor: 'pointer',
                fontSize: '0.82rem',
                fontWeight: 600,
                color: 'var(--muted)',
                whiteSpace: 'nowrap',
            }}
        >
            {show[showKey] ? 'Hide' : 'Show'}
        </button>
    );

    return (
        <div className="auth-page">
            <div
                className="auth-card"
                style={{ width: 'min(480px, 100%)', gap: 14 }}
            >
                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: 4 }}>
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 52,
                        height: 52,
                        borderRadius: '50%',
                        background: '#fff4e5',
                        marginBottom: 12,
                    }}>
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                    </div>
                    <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Password Change Required</h2>
                    <p style={{ marginTop: 6, color: 'var(--muted)', fontSize: '0.9rem' }}>
                        Your access was provisioned or reset by the master admin.
                        Set a new personal password to continue.
                    </p>
                    <p style={{
                        marginTop: 8,
                        fontSize: '0.82rem',
                        background: '#fff4e5',
                        border: '1px solid #f59e0b',
                        borderRadius: 8,
                        padding: '7px 12px',
                        color: '#92400e',
                    }}>
                        Logged in as <strong>{user?.email}</strong>
                    </p>
                </div>

                {error && (
                    <div className="error-banner">{error}</div>
                )}

                <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 12 }}>
                    {/* Current password */}
                    <div>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '0.875rem' }}>
                            Current (temporary) password
                        </label>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <input
                                type={show.current ? 'text' : 'password'}
                                value={form.currentPassword}
                                onChange={handleChange('currentPassword')}
                                autoComplete="current-password"
                                placeholder="Enter the password you received"
                                required
                                style={{ flex: 1 }}
                            />
                            {showToggleButton('current', 'current')}
                        </div>
                    </div>

                    {/* New password */}
                    <div>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '0.875rem' }}>
                            New password
                        </label>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <input
                                type={show.next ? 'text' : 'password'}
                                value={form.newPassword}
                                onChange={handleChange('newPassword')}
                                autoComplete="new-password"
                                placeholder="Minimum 6 characters"
                                required
                                style={{ flex: 1 }}
                            />
                            {showToggleButton('next', 'next')}
                        </div>

                        {/* Strength meter */}
                        {form.newPassword ? (
                            <div style={{ marginTop: 8 }}>
                                <div style={{
                                    height: 6,
                                    borderRadius: 999,
                                    background: '#e5e7eb',
                                    overflow: 'hidden',
                                }}>
                                    <div style={{
                                        width: `${strength.pct}%`,
                                        height: '100%',
                                        background: strength.color,
                                        transition: 'width 180ms ease',
                                    }} />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                                    <small style={{ color: strength.color, fontWeight: 600 }}>
                                        {strength.label}
                                    </small>
                                    <small style={{ color: 'var(--muted)' }}>
                                        8+ chars · A–z · 0–9 · symbols
                                    </small>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {/* Confirm password */}
                    <div>
                        <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: '0.875rem' }}>
                            Confirm new password
                        </label>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <input
                                type={show.confirm ? 'text' : 'password'}
                                value={form.confirmPassword}
                                onChange={handleChange('confirmPassword')}
                                autoComplete="new-password"
                                placeholder="Re-enter new password"
                                required
                                style={{ flex: 1 }}
                            />
                            {showToggleButton('confirm', 'confirm')}
                        </div>
                        {form.confirmPassword && form.newPassword !== form.confirmPassword ? (
                            <small style={{ color: '#ef4444', marginTop: 4, display: 'block' }}>
                                Passwords do not match
                            </small>
                        ) : form.confirmPassword && form.newPassword === form.confirmPassword ? (
                            <small style={{ color: '#10b981', marginTop: 4, display: 'block' }}>
                                Passwords match
                            </small>
                        ) : null}
                    </div>

                    <button
                        className="btn"
                        type="submit"
                        disabled={loading}
                        style={{ marginTop: 4 }}
                    >
                        {loading ? 'Updating password…' : 'Set new password & continue'}
                    </button>
                </form>

                <p className="auth-link" style={{ marginTop: 8, fontSize: '0.82rem' }}>
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={handleLogout}
                        onKeyDown={(e) => e.key === 'Enter' && handleLogout()}
                        style={{ cursor: 'pointer', color: 'var(--muted)', textDecoration: 'underline' }}
                    >
                        Sign out and use a different account
                    </span>
                </p>
            </div>
        </div>
    );
}
